<script setup lang="ts">
import {
  alertController,
  IonButton,
  IonContent,
  IonIcon,
  IonInput,
  IonItem,
  IonList,
  IonPage,
  IonSpinner,
} from "@ionic/vue";
import { isUnauthorizedResponseError } from "@packages/api";
import { keyOutline, mailOutline } from "ionicons/icons";
import { z } from "zod";

import AppVersion from "@/components/AppVersion.vue";
import FullLogo from "@/components/FullLogo.vue";
import { useForm, useQueryParams } from "@/composables";
import { router } from "@/router";
import { useUserStore } from "@/stores";

const userStore = useUserStore();

const queryParams = useQueryParams(
  z.object({
    email: z.string().default(""),
    redirect: z.string().optional(),
  }),
);

const form = useForm({
  email: queryParams.email,
  password: "",
});

async function onLogin() {
  try {
    await userStore.login(form.data);
    await router.replace(queryParams.redirect ?? { name: "library" });

    form.reset();
  } catch (error) {
    if (isUnauthorizedResponseError(error)) {
      const dialog = await alertController.create({
        header: "Login failed",
        message: error.message,
        buttons: ["OK"],
      });

      await dialog.present();
      return;
    }

    throw error;
  }
}

async function onResetPassword() {
  await router.push({
    name: "reset-password",
    query: {
      email: form.data.email,
    },
  });
}
</script>

<template>
  <IonPage>
    <IonContent fullscreen class="safe-p">
      <div class="login-page">
        <div class="login-page__header">
          <FullLogo />
        </div>
        <div class="login-page__body">
          <form @submit.prevent="() => onLogin()">
            <IonList lines="none">
              <IonItem class="ion-margin-bottom">
                <IonIcon slot="start" :icon="mailOutline" color="medium" />
                <IonInput
                  v-model="form.data.email"
                  autofocus
                  type="email"
                  autocomplete="email"
                  label="Email"
                  label-placement="floating"
                  :disabled="userStore.loginRequest.isPending"
                />
              </IonItem>
              <IonItem class="ion-margin-bottom">
                <IonIcon slot="start" :icon="keyOutline" color="medium" />
                <IonInput
                  v-model="form.data.password"
                  type="password"
                  label="Password"
                  label-placement="floating"
                  :disabled="userStore.loginRequest.isPending"
                />
              </IonItem>
            </IonList>
            <IonButton
              class="ion-margin-top"
              expand="block"
              color="success"
              type="submit"
              :disabled="userStore.loginRequest.isPending"
            >
              <IonSpinner
                v-if="userStore.loginRequest.isPending"
                slot="start"
                name="lines-small"
                class="ion-margin-end"
              />
              Log in
            </IonButton>
            <IonButton
              expand="block"
              fill="clear"
              color="medium"
              type="button"
              @click="() => onResetPassword()"
            >
              Forgot password?
            </IonButton>
          </form>
        </div>
        <AppVersion class="login-page__version" />
      </div>
    </IonContent>
  </IonPage>
</template>

<style lang="scss" scoped>
.login-page {
  display: grid;
  max-width: 560px;
  height: 100%;
  margin-inline: auto;
  gap: 16px;

  &__header {
    display: flex;
    justify-content: center;
  }

  &__body {
    padding: 32px;
  }

  &__version {
    place-self: end center;
    padding: 16px;
  }
}

ion-item {
  --background: var(--ion-color-light-tint);

  html.ios & {
    --border-radius: 14px;
  }

  html.md & {
    --border-radius: 4px;
  }
}
</style>
